import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import styled from 'styled-components'
import { ResidentNeedV6, useResidentNeedUnassign } from '../../../../../hooks/useResidentNeeds'

type ResidentNeedDeleteModalProps = {
  isModalVisible: boolean
  need: ResidentNeedV6 | null
  residentId: string
  onClose: () => void
  testId?: string
}

export const ResidentNeedDeleteModal: FC<ResidentNeedDeleteModalProps> = ({
  isModalVisible,
  need,
  residentId,
  onClose,
  testId,
}) => {
  const { t } = useTranslation()
  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative?: string } } }) =>
      state.global.home.preferredCarePlansNominative,
  )
  const { unassignNeed, isUnassigning } = useResidentNeedUnassign(residentId, onClose)

  const onConfirm = async () => {
    if (!need) return
    await unassignNeed(need.id)
  }

  return (
    <StyledModal
      localisation={{
        title: t('skillsAndNeeds.unassignNeedModalTitle'),
        confirmText: t('skillsAndNeeds.unassignNeedConfirm'),
        cancelText: t('general.buttons.cancel'),
      }}
      onConfirm={onConfirm}
      onCancel={onClose}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: isUnassigning, variant: 'danger' }}
      testId={testId}
    >
      <Divider style={{ margin: '0' }} />
      <StyledContent>
        <Text level={1} weight="medium">
          {t('skillsAndNeeds.unassignNeedBody')}
        </Text>
        <StyledWarningBlock>
          <Text level={1}>{t('skillsAndNeeds.unassignNeedWarning', { carePlansNominative: (carePlanNominative ?? 'support plans').toLowerCase() })}</Text>
        </StyledWarningBlock>
      </StyledContent>
    </StyledModal>
  )
}

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
`

const StyledWarningBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: -4px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }
`

export default ResidentNeedDeleteModal

